import { usePromo } from '@/context/PromoContext';
import { useEffect, useLayoutEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { getCart } from '@/queries/cart.queries';
import { CULT_SKU_ID, CULT_BENEFIT_ID } from '@/constants';
import { IS_DEVELOPMENT_ENV } from '@/AppConfig';
import { buildCultBotContext } from '@/utils/buildCultBotContext';
import MaintenancePage from './MaintenancePage';
// import { CouponLoaderBottomSheet } from '@/components/promos/cult-fit/CouponBottomSheets';

const BaseCult = () => {
  const {
    setEnabledQueries,
    cult: {
      selectedCity = null,
      setSelectedCity = () => {},
      showMaintenance = false,
      queries: { cultSkuDetails = null, querySkuMembership = null } = {},
    } = {},
  } = usePromo() ?? {};

  const navigate = useNavigate();
  const location = useLocation();
  const { data: cartData = { cart: { items: [] } }, isLoading: isCartLoading } =
    useQuery(getCart());

  useEffect(() => {
    setEnabledQueries?.({
      cultPromoDetails: true,
      // querySkuCount: true,
      querySkuCount: false,
      querySkuMembership: true,
    });
  }, []);

  useEffect(() => {
    if (cultSkuDetails?.data && selectedCity == null) {
      const city = cultSkuDetails?.data?.pricing?.[0];
      setSelectedCity?.(city?.id ?? null);
    }
  }, [cultSkuDetails?.data]);

  // The cult bot widget runs the v3 cult_support_bot, which activates on the
  // top-level `cult` key of the shared bot-context cookie (the backend
  // self-fetches membership from OMS via the JWT). Seed it on the parent domain
  // (.plumhq.com) so the chat-framework iframe can read it. Mirrors the cashless
  // status cookie-seed; the same VITE_VERCEL_ENV='preview' gotcha applies for
  // hosted staging builds.
  //
  // useLayoutEffect (not useEffect) so the cult context is written before paint,
  // ahead of the floating iframe's async session validation. The cookie is
  // shared across v3 bots on .plumhq.com, so seeding as early as possible
  // minimises the window where the cult session could read a prior bot's
  // context on a client-side navigation into the cult subtree.
  useLayoutEffect(() => {
    Cookies.set('bot-context', JSON.stringify(buildCultBotContext()), {
      domain: IS_DEVELOPMENT_ENV ? 'localhost' : '.plumhq.com',
      secure: !IS_DEVELOPMENT_ENV,
    });
  }, []);

  useEffect(() => {
    // Only proceed with navigation logic if both membership and cart queries have finished loading
    if (querySkuMembership?.isLoading || isCartLoading) {
      return;
    }

    // Membership check takes precedence - if user has membership, redirect to status
    if (
      querySkuMembership?.data?.isMembershipPresent &&
      location.pathname !== '/retail/cult-fit/status'
    ) {
      navigate('/retail/cult-fit/status?status=SUCCESS');
      return;
    }

    // Only check cart if no membership is present
    if (
      !querySkuMembership?.data?.isMembershipPresent &&
      cartData?.cart?.items
    ) {
      const hasCultSku = cartData.cart.items.some(
        (item) =>
          item.sku === CULT_SKU_ID || item.benefitId === CULT_BENEFIT_ID,
      );

      // Navigate to landing if no cult SKU in cart and currently on checkout
      if (!hasCultSku && location.pathname === '/retail/cult-fit/checkout') {
        navigate('/retail/cult-fit');
      }
    }
  }, [
    querySkuMembership?.data,
    querySkuMembership?.isLoading,
    cartData?.cart?.items,
    isCartLoading,
    location.pathname,
    navigate,
  ]);

  if (showMaintenance) {
    return <MaintenancePage />;
  }

  return (
    <>
      <Outlet />
      {/* <CouponLoaderBottomSheet /> */}
    </>
  );
};

export default BaseCult;
